import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { Field, Skeleton, RegPlate, Icon, StatusPill, SectionHeader } from '../components/ui';
import { api, type Asset, type Customer, type Invoice } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

interface Payload {
  customer: Customer;
  assets: Asset[];
  invoices: Invoice[];
}

const EMPTY = {
  name: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  postcode: '',
  oLicenceNo: '',
  oLicenceExpiry: '',
  notes: '',
};

export default function CustomerForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { busy, error, run } = useAction();
  const [form, setForm] = useState(EMPTY);
  const [editMode, setEditMode] = useState(!editing);

  const { data, loading, reload } = useLoad<Payload | null>(
    () => (editing ? api.get(`/customers/${id}`) : Promise.resolve(null)),
    [id],
  );

  useEffect(() => {
    const c = data?.customer;
    if (!c) return;
    setForm({
      name: c.name ?? '',
      contactName: c.contact_name ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      address: c.address ?? '',
      postcode: c.postcode ?? '',
      oLicenceNo: c.o_licence_no ?? '',
      oLicenceExpiry: c.o_licence_expiry ?? '',
      notes: c.notes ?? '',
    });
  }, [data]);

  const set =
    (key: keyof typeof EMPTY) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      if (editing) {
        await api.patch(`/customers/${id}`, form);
        setEditMode(false);
        reload();
      } else {
        const result = await api.post<{ customer: Customer }>('/customers', form);
        navigate(`/customers/${result.customer.id}`, { replace: true });
      }
    });
  };

  if (editing && loading && !data) {
    return (
      <Screen title="Customer" back="/customers">
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }

  return (
    <Screen
      title={editing ? (data?.customer.name ?? 'Customer') : 'Add customer'}
      back="/customers"
      action={
        editing && !editMode ? (
          <button className="btn btn-ghost btn-sm" onClick={() => setEditMode(true)}>
            Edit
          </button>
        ) : undefined
      }
    >
      <div className="stack-5">
        {error ? <div className="notice notice-danger">{error}</div> : null}

        {editMode ? (
          <form className="stack-4" onSubmit={submit}>
            <Field label="Customer name">
              <input className="input" required value={form.name} onChange={set('name')} />
            </Field>
            <div className="grid-2 collapse">
              <Field label="Contact">
                <input className="input" value={form.contactName} onChange={set('contactName')} />
              </Field>
              <Field label="Phone">
                <input className="input" type="tel" inputMode="tel" value={form.phone} onChange={set('phone')} />
              </Field>
            </div>
            <Field label="Email">
              <input
                className="input"
                type="email"
                inputMode="email"
                autoCapitalize="off"
                value={form.email}
                onChange={set('email')}
              />
            </Field>
            <Field label="Address">
              <textarea className="textarea" rows={2} value={form.address} onChange={set('address')} />
            </Field>
            <Field label="Postcode">
              <input className="input input-mono" value={form.postcode} onChange={set('postcode')} />
            </Field>
            <div className="grid-2 collapse">
              <Field label="O-Licence number">
                <input className="input input-mono" value={form.oLicenceNo} onChange={set('oLicenceNo')} />
              </Field>
              <Field label="O-Licence expiry">
                <input className="input" type="date" value={form.oLicenceExpiry} onChange={set('oLicenceExpiry')} />
              </Field>
            </div>
            <Field label="Notes">
              <textarea className="textarea" rows={3} value={form.notes} onChange={set('notes')} />
            </Field>

            <div className="actionbar">
              {editing ? (
                <button type="button" className="btn btn-ghost" onClick={() => setEditMode(false)}>
                  Cancel
                </button>
              ) : (
                <Link to="/customers" className="btn btn-ghost">
                  Cancel
                </Link>
              )}
              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? 'Saving…' : editing ? 'Save changes' : 'Add customer'}
              </button>
            </div>
          </form>
        ) : data ? (
          <>
            <div className="card card-pad">
              <dl className="kv">
                {data.customer.contact_name ? (
                  <>
                    <dt>Contact</dt>
                    <dd>{data.customer.contact_name}</dd>
                  </>
                ) : null}
                {data.customer.phone ? (
                  <>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${data.customer.phone}`}>{data.customer.phone}</a>
                    </dd>
                  </>
                ) : null}
                {data.customer.email ? (
                  <>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${data.customer.email}`}>{data.customer.email}</a>
                    </dd>
                  </>
                ) : null}
                {data.customer.address ? (
                  <>
                    <dt>Address</dt>
                    <dd>
                      {data.customer.address}
                      {data.customer.postcode ? `, ${data.customer.postcode}` : ''}
                    </dd>
                  </>
                ) : null}
                {data.customer.o_licence_no ? (
                  <>
                    <dt>O-Licence</dt>
                    <dd className="mono">
                      {data.customer.o_licence_no}
                      {data.customer.o_licence_expiry ? (
                        <span className="dim"> · expires {formatDate(data.customer.o_licence_expiry)}</span>
                      ) : null}
                    </dd>
                  </>
                ) : null}
                {data.customer.notes ? (
                  <>
                    <dt>Notes</dt>
                    <dd>{data.customer.notes}</dd>
                  </>
                ) : null}
              </dl>
            </div>

            <section className="stack">
              <SectionHeader
                title="Vehicles"
                action={
                  <Link to="/fleet/new">
                    Add <Icon name="chevron" size={14} />
                  </Link>
                }
              />
              {data.assets.length === 0 ? (
                <p className="small muted">No vehicles on this account yet.</p>
              ) : (
                <div className="panel divide">
                  {data.assets.map((asset) => (
                    <Link key={asset.id} to={`/fleet/${asset.id}`} className="listrow">
                      <div className="listrow-lead">
                        <RegPlate reg={asset.reg_no} fleetNo={asset.fleet_no} size="sm" />
                      </div>
                      <div className="grow">
                        <div className="listrow-title truncate">
                          {[asset.make, asset.model].filter(Boolean).join(' ') || 'Vehicle'}
                        </div>
                        <div className="listrow-sub">
                          {asset.next_inspection_date ? `PMI ${formatDate(asset.next_inspection_date)}` : 'Not scheduled'}
                        </div>
                      </div>
                      {asset.status && asset.status.state !== 'ok' && asset.status.state !== 'unknown' ? (
                        <StatusPill state={asset.status.state} size="sm">
                          {asset.status.label}
                        </StatusPill>
                      ) : null}
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {data.invoices.length > 0 && (
              <section className="stack">
                <SectionHeader title="Invoices" />
                <div className="panel divide">
                  {data.invoices.map((invoice) => (
                    <Link key={invoice.id} to={`/invoices/${invoice.id}`} className="listrow">
                      <div className="listrow-lead" style={{ minWidth: '5rem' }}>
                        <span className="mono small">{invoice.number}</span>
                      </div>
                      <div className="grow">
                        <div className="listrow-title">{formatDate(invoice.issue_date)}</div>
                      </div>
                      <span className="money">£{Number(invoice.total).toFixed(2)}</span>
                      <StatusPill state={invoice.status === 'paid' ? 'ok' : 'due'} size="sm">
                        {invoice.status}
                      </StatusPill>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        ) : null}
      </div>
    </Screen>
  );
}
